import { Eye, EyeOff } from 'lucide-react';
import type { PathFilterMode } from '@/types/api';

export interface PathFilterToggleProps {
  /** Current filter mode */
  value: PathFilterMode;
  /** Callback when mode changes */
  onChange: (mode: PathFilterMode) => void;
  /** Whether the toggle is visible (only show when a path is active) */
  isActive: boolean;
  /** Optional additional className */
  className?: string;
}

/**
 * PathFilterToggle molecule - toggles between dim and hide modes for non-path items
 *
 * When a dependency path is active:
 * - Dim mode: Non-path items are faded but visible
 * - Hide mode: Non-path items are completely hidden
 */
export function PathFilterToggle({ value, onChange, isActive, className = '' }: PathFilterToggleProps) {
  if (!isActive) return null;

  const isDimMode = value === 'dim';

  return (
    <div
      className={`
        flex items-center gap-2
        ${className}
      `}
    >
      <span className="text-xs text-text-muted">Others:</span>
      <button
        type="button"
        onClick={() => onChange(isDimMode ? 'hide' : 'dim')}
        className={`
          flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition-colors duration-200
          ${
            isDimMode
              ? `
                border-border-default bg-bg-base text-text-secondary
                hover:bg-bg-subtle
              `
              : `
                border-action-primary/30 bg-action-primary/10 text-action-primary
                hover:bg-action-primary/20
              `
          }
        `}
        title={isDimMode ? 'Click to hide non-path items' : 'Click to show dimmed non-path items'}
      >
        {isDimMode ? (
          <>
            <Eye className="size-3.5" />
            <span>Dimmed</span>
          </>
        ) : (
          <>
            <EyeOff className="size-3.5" />
            <span>Hidden</span>
          </>
        )}
      </button>
    </div>
  );
}
